import * as React from "react";
import PropTypes from "prop-types";
import { ChartColors } from "../../theme";
import { withStyles, Typography } from "@material-ui/core";
import { CyNumber } from "../../utilities/number";
import moment from "moment";
import ResponsiveContainer from "recharts/es6/component/ResponsiveContainer";
import LineChart from "recharts/es6/chart/LineChart";
import XAxis from "recharts/es6/cartesian/XAxis";
import YAxis from "recharts/es6/cartesian/YAxis";
import CartesianGrid from "recharts/es6/cartesian/CartesianGrid";
import Legend from "recharts/es6/component/Legend";
import Line from "recharts/es6/cartesian/Line";
import Tooltip from "recharts/es6/component/Tooltip";
import { AppTheme } from "../../theme";
import { connect } from "react-redux";

const styles = (theme) => ({
  wrapper: {
    position: "relative",
    width: "100%",
    breakInside: "avoid",
    "& tspan": {
      [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
        fontSize: 12,
      },
    },
    "& .recharts-cartesian-axis-ticks": {
      fontSize: 14,
    },
    "& .legend-wrapper": {
      width: "20%",
      float: "right",
    },
  },
  chartSubtitle: {
    marginBottom: 20,
    textAlign: "center",
    [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
      fontSize: 13,
    },
    "&.print": {
      textAlign: "left",
    },
  },
  label: {
    position: "absolute",
    top: 25,
    right: 50,
    backgroundColor: "white",
    border: `1px solid ${theme.palette.grey["A100"]}`,
    whiteSpace: "nowrap",
    padding: 10,
    zIndex: 99,
  },
  legend: {
    float: "right",
    marginTop: 14,
    "@media print": {
      float: "none",
      textAlign: "center",
    },
  },
  legendItem: {
    textAlign: "right",
    display: "block",
    position: "relative",
    color: theme.palette.grey[600],
    paddingRight: 44,
    fontSize: 14,
    "&.green": {
      color: theme.palette.primary.main,
    },
    "&:not(:first-child)": {
      marginLeft: 20,
    },
    [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
      fontSize: 13,
    },
  },
  legendChip: {
    position: "absolute",
    display: "block",
    right: 0,
    top: 5,
    width: 33,
    height: 11,
    backgroundColor: theme.palette.grey[600],
    "-webkit-print-color-adjust": "exact",
    "&.green": {
      backgroundColor: theme.palette.primary.main,
    },
    "&.print": {
      position: "relative",
      display: "inline-block",
      verticalAlign: "top",
    },
  },
});

const CyRiskXTick = (props) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x + 25},${y})`}>
      <text
        fontSize={12}
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill={AppTheme.palette.grey[700]}
        transform="rotate(0)"
      >
        {moment(payload.value).format("MMM YYYY")}
      </text>
    </g>
  );
};

class CyLineChart extends React.Component {
  state = {
    label: true,
  };

  toggleLabel = () => {
    this.setState({ label: !this.state.label });
  };

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      nextProps.data[0].Cy !== this.props.data[0].Cy ||
      nextProps.showTooltip !== this.props.showTooltip ||
      nextState.label !== this.state.label
    );
  }

  showLabel = () => {
    this.setState({ label: true });
  };

  hideLabel = () => {
    if (this.state.label) {
      this.setState({ label: false });
    }
  };

  renderTooltipLabel = (value) => {
    return moment(value).format("MMM DD, YYYY");
  };

  renderLegend = (props) => {
    const { payload } = props;
    const { classes, withAnnualWithdrawal, benchmarkName } =
      this.props;
    return (
      <div className={classes.legend}>
        {payload.map((entry, index) => (
          <span
            className={`${classes.legendItem} ${
              entry.value === "Cy" ? "green" : ""
            }`}
            key={`item-${index}`}
          >
            <span
              className={`${classes.legendChip} ${
                entry.value === "Cy" ? "green" : ""
              }`}
            />
            {withAnnualWithdrawal ? (
              entry.value === "Cy" ? (
                <React.Fragment>
                  Cy Optimized Portfolio with Annual Withdrawal{" "}
                  <sup>*</sup>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {benchmarkName} with Annual Withdrawal <sup>**</sup>
                </React.Fragment>
              )
            ) : entry.value === "Cy" ? (
              <React.Fragment>
                Cy Optimized Portfolio <sup>*</sup>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {benchmarkName} <sup>**</sup>
              </React.Fragment>
            )}
          </span>
        ))}
      </div>
    );
  };

  render() {
    const {
      data,
      classes,
      renderForPdfOutput,
      withAnnualWithdrawal,
      benchmarkName,
      initialInvestment,
    } = this.props;
    const { CurrencyFormatter } = CyNumber;
    const initVal = {
      [benchmarkName]: initialInvestment,
      Cy: initialInvestment,
      date: moment(data[0].date)
        .subtract(3, "months")
        .endOf("month")
        .add(1, "days")
        .format("YYYY-MM-DD"),
    };
    const chartData = [
      initVal,
      ...data.map((d) => {
        return { [benchmarkName]: d.Benchmark, Cy: d.Cy, date: d.date };
      }),
    ];
    const chartColors = ChartColors();
    const ticks =
      chartData.length >= 15
        ? chartData
            .map((d) => (moment(d.date).month() + 1 === 12 ? d.date : null))
            .filter(Boolean)
        : chartData.map((d) => d.date);
    const chartHeight = 320;
    const ChartContainer = renderForPdfOutput ? "div" : ResponsiveContainer;

    return (
      <div className={classes.wrapper}>
        {/*
        showTooltip && data && this.state.label &&
        <div className={`recharts-default-tooltip ${classes.label}`}>
          <Typography variant="body2">{moment(data[data.length-1].date).format('MMM DD, YYYY')}</Typography>
          <Typography variant="body2" className='recharts-tooltip-label' style={{color: labelColors.next().value}}>Cy: {CurrencyFormatter(data[data.length-1]['Cy'])}</Typography>
          <Typography variant="body2" className='recharts-tooltip-label' style={{color: labelColors.next().value}}>S&P 500: {CurrencyFormatter(data[data.length-1]['Benchmark'])}</Typography>
        </div>
      */}
        <Typography
          variant={"h5"}
          color={"primary"}
          align={renderForPdfOutput ? "left" : "center"}
        >
          {withAnnualWithdrawal
            ? `Ending Capital with Withdrawals`
            : `Growth of Investment`}
        </Typography>
        <Typography
          variant={renderForPdfOutput ? "h6" : "body1"}
          className={`${classes.chartSubtitle} ${
            renderForPdfOutput && "print"
          }`}
        >
          {withAnnualWithdrawal
            ? `Cy Optimized Portfolio vs. ${benchmarkName} with Annual Withdrawal`
            : `Cy Optimized Portfolio vs. ${benchmarkName}`}
        </Typography>
        <ChartContainer
          {...(renderForPdfOutput
            ? { style: { width: "80%", float: "left" } }
            : { width: "100%", height: chartHeight })}
        >
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 40, left: 50, bottom: 30 }}
            onMouseEnter={this.hideLabel}
            onMouseMove={this.hideLabel}
            onMouseLeave={this.showLabel}
            // If rendering to a PDF, remove margins and set an explicit width
            {...(renderForPdfOutput
              ? {
                  width: 700,
                  height: 200,
                  margin: {},
                }
              : {})}
          >
            <XAxis dataKey="date" tick={<CyRiskXTick />} ticks={ticks} />
            <YAxis tickFormatter={CurrencyFormatter} />
            <CartesianGrid vertical={false} />
            <Tooltip
              formatter={CurrencyFormatter}
              itemSorter={() => 1}
              labelFormatter={this.renderTooltipLabel}
            />
            {!renderForPdfOutput && <Legend content={this.renderLegend} />}
            <Line
              dataKey="Cy"
              strokeWidth={3}
              stroke={chartColors.next().value}
              dot={false}
              isAnimationActive={!renderForPdfOutput}
            />
            <Line
              dataKey={benchmarkName}
              strokeWidth={3}
              stroke={chartColors.next().value}
              dot={false}
              onMouseEnter={this.toggleLabel}
              onMouseLeave={this.toggleLabel}
              isAnimationActive={!renderForPdfOutput}
            />
          </LineChart>
        </ChartContainer>
        {renderForPdfOutput && (
          <div className={"legend-wrapper"}>
            <div style={{ marginBottom: "0.2in" }}>
              <span className={`${classes.legendChip} green print`} />
              <Typography variant={"body2"}>
                {withAnnualWithdrawal
                  ? `Cy Optimized Portfolio with Annual Withdrawal`
                  : `Cy Optimized Portfolio`}
                <sup>*</sup>
              </Typography>
            </div>
            <div>
              <span className={`${classes.legendChip} print`} />
              <Typography variant={"body2"}>
                {withAnnualWithdrawal
                  ? `${benchmarkName} with Annual Withdrawal`
                  : benchmarkName}
                <sup>**</sup>
              </Typography>
            </div>
          </div>
        )}
      </div>
    );
  }
}

CyLineChart.propTypes = {
  data: PropTypes.array.isRequired,
  initialInvestment: PropTypes.number.isRequired,
  renderForPdfOutput: PropTypes.bool,
  withAnnualWithdrawal: PropTypes.bool,
};

CyLineChart.defaultProps = {
  withAnnualWithdrawal: false,
};

const mapStateToProps = (state) => {
  return {
    appName: state.app.appName,
  };
};

export default withStyles(styles, { withTheme: true })(
  connect(mapStateToProps)(CyLineChart)
);



// WEBPACK FOOTER //
// ./src/components/Charts/CyLineChart.jsx